// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
export interface GlossaryEntry {
  term: string;
  short: string;
  long: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  dsn: {
    term: 'DSN',
    short: 'Deep Space Network',
    long: 'NASA\'s Deep Space Network is a global array of giant radio antennas used to communicate with spacecraft beyond low-Earth orbit. The three complexes (Goldstone, Madrid, Canberra) are spaced roughly 120° apart so at least one can always point at any distant spacecraft as Earth rotates.',
  },
  complex: {
    term: 'Complex',
    short: 'Ground station site',
    long: 'A DSN complex is one of the three ground-station sites: Goldstone (USA), Madrid (Spain), or Canberra (Australia). Each complex has several antennas.',
  },
  dss: {
    term: 'DSS',
    short: 'Deep Space Station (antenna)',
    long: 'DSS = Deep Space Station. Each dish has a DSS number (e.g. DSS-43 is Canberra\'s 70-metre antenna — the only one that can command Voyager 2).',
  },
  dish: {
    term: 'Dish',
    short: 'Parabolic antenna',
    long: 'A large parabolic reflector antenna. DSN dishes range from 26 m to 70 m across. Bigger dishes can hear fainter signals from more distant spacecraft.',
  },
  telemetry: {
    term: 'Telemetry',
    short: 'Data from spacecraft to Earth',
    long: 'Science data and spacecraft health information transmitted from a spacecraft back to Earth ("downlink").',
  },
  command: {
    term: 'Command',
    short: 'Instructions to spacecraft',
    long: 'Commands sent from Earth up to a spacecraft to change its behaviour ("uplink").',
  },
  tracking: {
    term: 'Tracking',
    short: 'Measuring position',
    long: 'Precise measurement of a spacecraft\'s position and velocity, usually done by measuring round-trip light time and Doppler shift of its signal.',
  },
  uplink: {
    term: 'Uplink',
    short: 'Signal from Earth to spacecraft',
    long: 'A radio signal transmitted from a ground antenna up to a spacecraft. Always much more powerful than the downlink.',
  },
  downlink: {
    term: 'Downlink',
    short: 'Signal from spacecraft to Earth',
    long: 'A radio signal from a spacecraft back to a ground antenna. Typically extremely weak by the time it arrives (a billionth of a billionth of a watt for Voyager).',
  },
  sband: {
    term: 'S-band',
    short: '~2 GHz',
    long: 'S-band (2–4 GHz) is lower-frequency and less susceptible to weather. Used for low-rate telemetry and emergency links.',
  },
  xband: {
    term: 'X-band',
    short: '~8 GHz',
    long: 'X-band (8–12 GHz) is the DSN\'s workhorse frequency. Balance of bandwidth and atmospheric loss.',
  },
  kaband: {
    term: 'Ka-band',
    short: '~32 GHz',
    long: 'Ka-band (27–40 GHz) supports the highest data rates but is sensitive to rain and clouds. Used by JWST and other high-throughput missions.',
  },
  kband: {
    term: 'K-band',
    short: '~20 GHz',
    long: 'K-band (18–27 GHz) is used for some high-rate downlinks such as near-Earth missions.',
  },
  lband: {
    term: 'L-band',
    short: '~1.5 GHz',
    long: 'L-band (1–2 GHz) is lower frequency, used mainly for GPS and some near-Earth spacecraft.',
  },
  cband: {
    term: 'C-band',
    short: '~5 GHz',
    long: 'C-band (4–8 GHz) is rarely used by the DSN — more common in commercial satellite links.',
  },
  rtlt: {
    term: 'RTLT',
    short: 'Round-Trip Light Time',
    long: 'Round-Trip Light Time — how long a radio signal takes to get from Earth to the spacecraft and back. For Voyager 1 it is roughly 44 hours. Reported in seconds in the DSN feed.',
  },
  mspa: {
    term: 'MSPA',
    short: 'Multiple Spacecraft Per Antenna',
    long: 'A mode where one dish simultaneously receives downlinks from more than one spacecraft that happen to be angularly close on the sky. Saves antenna time.',
  },
  ddor: {
    term: 'DDOR',
    short: 'Delta-Differential One-way Ranging',
    long: 'A very precise navigation technique using two antennas simultaneously to measure the angle to a spacecraft relative to a quasar.',
  },
  azimuth: {
    term: 'Azimuth',
    short: 'Compass bearing',
    long: 'Where the dish is pointed measured clockwise from north: 0° = North, 90° = East, 180° = South, 270° = West.',
  },
  elevation: {
    term: 'Elevation',
    short: 'Angle above horizon',
    long: 'How high above the horizon the dish is pointed. 0° = flat on the horizon, 90° = straight up (zenith).',
  },
  datarate: {
    term: 'Data rate',
    short: 'Bits per second',
    long: 'How fast the spacecraft is sending (or receiving) data, in bits per second. Voyager\'s science downlink today is around 160 bps — a baud modem is faster.',
  },
  signalpower: {
    term: 'Signal power',
    short: 'Received signal strength (dBm)',
    long: 'How strong the signal is at the dish, in decibel-milliwatts (dBm). More negative numbers = weaker. Voyager signals arrive at about -160 dBm, roughly 10⁻¹⁹ watts.',
  },
  light: {
    term: 'Light time',
    short: 'Delay from speed of light',
    long: 'Radio waves travel at the speed of light (299,792 km/s). Even at that speed, a signal takes ~1.3 s to reach the Moon, ~4–20 min to reach Mars, and ~22 hours one-way to reach Voyager 1.',
  },
  voyager: {
    term: 'Voyager',
    short: 'Pair of interstellar probes',
    long: 'Voyager 1 and 2 were launched in 1977. Both have crossed the heliopause into interstellar space. Voyager 2\'s trajectory means only Canberra\'s DSS-43 can command it.',
  },
};

export function lookup(term: string): GlossaryEntry | null {
  return GLOSSARY[term.toLowerCase()] ?? null;
}
